import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { USER_STORAGE } from '@/constants/storage';
import { getApiErrorMessage, userAuthService } from '@/services';
import { notify } from '@/utils/notify';
import { userAccountService } from '@/services/users/userAccountService';
import type { LoginInput, RegisterInput } from '@/services/auth/userAuthService';
import type { PublicUser } from '@/types/auth';
import { clearAuthStorage, getStoredToken, persistTokens } from '@/utils/tokenSession';

interface UserAuthContextValue {
  user: PublicUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  pinSetupDismissed: boolean;
  login: (input: LoginInput) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  persistUser: (user: PublicUser) => void;
  dismissPinSetup: () => void;
  clearPinDismissal: () => void;
  clearError: () => void;
  setUser: (user: PublicUser) => void;
}

const UserAuthContext = createContext<UserAuthContextValue | null>(null);

function readStoredUser(): PublicUser | null {
  const raw = localStorage.getItem(USER_STORAGE.user);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PublicUser;
  } catch {
    return null;
  }
}

function readPinDismissed(): boolean {
  return sessionStorage.getItem(USER_STORAGE.pinDismissed) === '1';
}

export function UserAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<PublicUser | null>(() => readStoredUser());
  const [token, setToken] = useState<string | null>(() => getStoredToken('user'));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pinSetupDismissed, setPinSetupDismissed] = useState(readPinDismissed);

  const persistSession = useCallback((nextUser: PublicUser, accessToken: string, refreshToken: string) => {
    persistTokens('user', accessToken, refreshToken);
    localStorage.setItem(USER_STORAGE.user, JSON.stringify(nextUser));
    setUserState(nextUser);
    setToken(accessToken);
  }, []);

  const persistUser = useCallback((nextUser: PublicUser) => {
    localStorage.setItem(USER_STORAGE.user, JSON.stringify(nextUser));
    setUserState(nextUser);
    if (nextUser.pinEnabled) {
      sessionStorage.removeItem(USER_STORAGE.pinDismissed);
      setPinSetupDismissed(false);
    }
  }, []);

  const dismissPinSetup = useCallback(() => {
    sessionStorage.setItem(USER_STORAGE.pinDismissed, '1');
    setPinSetupDismissed(true);
  }, []);

  const clearPinDismissal = useCallback(() => {
    sessionStorage.removeItem(USER_STORAGE.pinDismissed);
    setPinSetupDismissed(false);
  }, []);

  const login = useCallback(async (input: LoginInput) => {
    setIsLoading(true);
    setError(null);
    clearPinDismissal();
    try {
      const res = await userAuthService.login(input);
      if (!res.success || !res.data) throw new Error(res.message || 'Login failed');
      persistSession(res.data.user, res.data.tokens.accessToken, res.data.tokens.refreshToken);
    } catch (err) {
      const message = getApiErrorMessage(err, 'Invalid credentials. Please try again.');
      setError(message);
      notify.error(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [clearPinDismissal, persistSession]);

  const register = useCallback(async (input: RegisterInput) => {
    setIsLoading(true);
    setError(null);
    clearPinDismissal();
    try {
      const res = await userAuthService.register(input);
      if (!res.success || !res.data) throw new Error(res.message || 'Registration failed');
      persistSession(res.data.user, res.data.tokens.accessToken, res.data.tokens.refreshToken);
    } catch (err) {
      const message = getApiErrorMessage(err, 'Could not create your account. Please try again.');
      setError(message);
      notify.error(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [clearPinDismissal, persistSession]);

  const logout = useCallback(async () => {
    const accessToken = getStoredToken('user');
    clearAuthStorage('user');
    sessionStorage.removeItem(USER_STORAGE.pinDismissed);
    setUserState(null);
    setToken(null);
    setPinSetupDismissed(false);
    if (accessToken) {
      void userAuthService.logout(accessToken).catch(() => undefined);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    const res = await userAccountService.getProfile();
    if (res.success && res.data?.user) {
      persistUser(res.data.user);
    }
  }, [persistUser]);

  const value = useMemo<UserAuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(user && token),
      isLoading,
      error,
      pinSetupDismissed,
      login,
      register,
      logout,
      refreshProfile,
      persistUser,
      dismissPinSetup,
      clearPinDismissal,
      clearError: () => setError(null),
      setUser: setUserState,
    }),
    [user, token, isLoading, error, pinSetupDismissed, login, register, logout, refreshProfile, persistUser, dismissPinSetup, clearPinDismissal],
  );

  return <UserAuthContext.Provider value={value}>{children}</UserAuthContext.Provider>;
}

export function useUserAuth() {
  const ctx = useContext(UserAuthContext);
  if (!ctx) throw new Error('useUserAuth must be used within UserAuthProvider');
  return ctx;
}
